import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Reveal, Field, inputCls } from "@/components/ui-bits";

const TEXT_FIELDS = [
  ["full_name", "form.fullName", true], ["email", "form.email", true, "email"],
  ["phone", "form.phone"], ["whatsapp", "form.whatsapp"],
  ["country", "form.country", true], ["city", "form.city"],
  ["job_title", "join.currentJobTitle"], ["employer", "join.currentEmployer"],
  ["highest_degree", "join.highestDegree"], ["university", "join.university"],
  ["specialization", "join.specialization"], ["years_experience", "join.yearsExperience", false, "number"],
  ["expertise_areas", "join.expertiseAreas", false, "text", true],
  ["key_skills", "join.keySkills", false, "text", true],
  ["languages", "join.languages", false, "text", true],
  ["linkedin", "join.linkedin"], ["google_scholar", "join.scholar"], ["orcid", "join.orcid"],
  ["work_regions", "join.workRegions", false, "text", true],
];

const AREA_FIELDS = [
  ["qualifications", "join.qualifications"],
  ["training_experience", "join.trainingExp"],
  ["consulting_experience", "join.consultingExp"],
  ["sector_experience", "join.sectorExp"],
  ["bio", "join.bio"],
  ["notes", "join.notes"],
];

export default function Join() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ online_availability: "yes", onsite_availability: "yes", consultation_type: "expert" });
  const [cv, setCv] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!cv) return toast.error(t("join.cv"));
    if (!consent) return toast.error(t("join.consent"));
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("consent", consent ? "true" : "false");
      fd.append("cv", cv);
      if (photo) fd.append("photo", photo);
      await api.post("/expert-applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success(t("join.success"));
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (done)
    return (
      <div className="mx-auto max-w-2xl px-4 py-32 text-center" data-testid="join-success">
        <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
        <h1 className="mt-6 font-display text-4xl font-semibold text-navy">{t("join.title")}</h1>
        <p className="mt-4 text-slate-600">{t("join.success")}</p>
        <Link to="/" className="mt-8 inline-block rounded-full bg-navy px-8 py-3.5 text-sm font-bold text-white">{t("notFound.cta")}</Link>
      </div>
    );

  return (
    <div data-testid="join-page">
      <section className="hero-grain relative overflow-hidden bg-navy-950 py-24 lg:py-28">
        <img src={IMAGES.strategy} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/50 to-navy-950" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-gold">{t("experts.eyebrow")}</p>
            <h1 className="max-w-3xl font-display text-4xl font-semibold text-white sm:text-5xl">{t("join.title")}</h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">{t("join.sub")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <form onSubmit={submit} className="mx-auto max-w-4xl px-4 sm:px-6" data-testid="expert-application-form">
          <Reveal className="rounded-lg border border-slate-200 bg-white p-7 shadow-sm sm:p-10">
            <h2 className="mb-6 border-b border-slate-100 pb-4 font-display text-2xl font-semibold text-navy">{t("join.section.personal")}</h2>
            <div className="grid gap-5 sm:grid-cols-2">
              {TEXT_FIELDS.slice(0, 6).map(([name, label, required, type]) => (
                <Field key={name} label={t(label)} required={required}>
                  <input required={required} type={type || "text"} className={inputCls} value={form[name] || ""} onChange={set(name)} data-testid={`join-${name}`} />
                </Field>
              ))}
            </div>

            <h2 className="mb-6 mt-10 border-b border-slate-100 pb-4 font-display text-2xl font-semibold text-navy">{t("join.section.professional")}</h2>
            <div className="mb-5">
              <Field label={t("join.consultType")} required>
                <select required className={inputCls} value={form.consultation_type} onChange={set("consultation_type")} data-testid="join-consultation-type">
                  <option value="expert">{t("join.typeExpert")}</option>
                  <option value="research">{t("join.typeResearch")}</option>
                  <option value="both">{t("join.typeBoth")}</option>
                </select>
              </Field>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              {TEXT_FIELDS.slice(6).map(([name, label, required, type, comma]) => (
                <Field key={name} label={t(label)} required={required} hint={comma ? t("join.commaHint") : undefined}>
                  <input required={required} type={type || "text"} min={type === "number" ? 0 : undefined} className={inputCls} value={form[name] || ""} onChange={set(name)} data-testid={`join-${name}`} />
                </Field>
              ))}
            </div>

            {(form.consultation_type === "research" || form.consultation_type === "both") && (
              <div data-testid="join-research-section">
                <h2 className="mb-6 mt-10 border-b border-slate-100 pb-4 font-display text-2xl font-semibold text-navy">{t("join.researchSection")}</h2>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label={t("join.researchAreas")} hint={t("join.commaHint")}>
                    <input className={inputCls} value={form.research_areas || ""} onChange={set("research_areas")} data-testid="join-research-areas" />
                  </Field>
                  <Field label={t("join.discipline")}>
                    <input className={inputCls} value={form.academic_discipline || ""} onChange={set("academic_discipline")} data-testid="join-discipline" />
                  </Field>
                  <Field label={t("join.methodologies")} hint={t("join.commaHint")}>
                    <input className={inputCls} value={form.research_methodologies || ""} onChange={set("research_methodologies")} data-testid="join-methodologies" />
                  </Field>
                  <Field label={t("join.supervision")}>
                    <input className={inputCls} value={form.supervision_experience || ""} onChange={set("supervision_experience")} data-testid="join-supervision" />
                  </Field>
                </div>
              </div>
            )}

            <h2 className="mb-6 mt-10 border-b border-slate-100 pb-4 font-display text-2xl font-semibold text-navy">{t("join.section.expertise")}</h2>
            <div className="grid gap-5">
              {AREA_FIELDS.slice(0, 4).map(([name, label]) => (
                <Field key={name} label={t(label)}>
                  <textarea rows={2} className={inputCls} value={form[name] || ""} onChange={set(name)} data-testid={`join-${name}`} />
                </Field>
              ))}
            </div>

            <h2 className="mb-6 mt-10 border-b border-slate-100 pb-4 font-display text-2xl font-semibold text-navy">{t("join.section.availability")}</h2>
            <div className="grid gap-5 sm:grid-cols-2">
              {[["online_availability", "join.onlineAvail"], ["onsite_availability", "join.onsiteAvail"]].map(([name, label]) => (
                <Field key={name} label={t(label)}>
                  <select className={inputCls} value={form[name]} onChange={set(name)} data-testid={`join-${name}`}>
                    <option value="yes">{t("join.yes")}</option>
                    <option value="no">{t("join.no")}</option>
                  </select>
                </Field>
              ))}
              <Field label={t("join.cv")} required>
                <label data-testid="join-cv-upload" className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-600 transition-colors hover:border-gold">
                  <UploadCloud className="h-5 w-5 text-gold-hover" />
                  <span className="truncate">{cv ? cv.name : "PDF / DOC / DOCX"}</span>
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setCv(e.target.files[0] || null)} />
                </label>
              </Field>
              <Field label={t("join.photo")}>
                <label data-testid="join-photo-upload" className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-600 transition-colors hover:border-gold">
                  <UploadCloud className="h-5 w-5 text-gold-hover" />
                  <span className="truncate">{photo ? photo.name : "JPG / PNG / WebP"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files[0] || null)} />
                </label>
              </Field>
            </div>
            <div className="mt-5 grid gap-5">
              {AREA_FIELDS.slice(4).map(([name, label]) => (
                <Field key={name} label={t(label)}>
                  <textarea rows={name === "bio" ? 4 : 2} className={inputCls} value={form[name] || ""} onChange={set(name)} data-testid={`join-${name}`} />
                </Field>
              ))}
            </div>

            <label className="mt-8 flex cursor-pointer items-start gap-3 rounded-lg bg-slate-50 p-4 text-sm text-slate-700" data-testid="join-consent-label">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#C5A059]"
                data-testid="join-consent-checkbox"
              />
              <span>
                {t("join.consent")}{" "}
                <Link to="/privacy" className="font-semibold text-gold-hover underline">{t("footer.privacy")}</Link>
              </span>
            </label>

            <button
              type="submit"
              disabled={sending}
              data-testid="expert-apply-submit-button"
              className="mt-8 w-full rounded-full bg-gold py-4 text-sm font-bold uppercase tracking-wider text-navy transition-colors hover:bg-gold-hover disabled:opacity-60 sm:w-auto sm:px-14"
            >
              {sending ? t("form.sending") : t("join.submit")}
            </button>
          </Reveal>
        </form>
      </section>
    </div>
  );
}
